import React, { useMemo } from 'react';
import { MapPin, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LOCATIONS, SERVICE_NAMES, getLocationsByRegion } from '../data/locations';
import './ServiceArea.css';

const ServiceArea = () => {
  const regionData = useMemo(() => getLocationsByRegion(), []);
  const totalLocationCount = useMemo(() => Object.keys(LOCATIONS).length, []);

  // Group regions logically
  const regionGroups = useMemo(() => {
    const groups = {
      'Kerngebiet': ['Nörten-Hardenberg', 'Landkreis Northeim'],
      'Göttingen & Umgebung': ['Landkreis Göttingen', 'Göttingen', 'Bovenden', 'Rosdorf', 'Gleichen', 'Friedland'],
      'Northeim & Umgebung': ['Northeim', 'Hardegsen', 'Moringen', 'Katlenburg-Lindau', 'Adelebsen', 'Dassel', 'Einbeck', 'Kalefeld'],
      'Eichsfeld & Harz': ['Duderstadt', 'Gieboldehausen', 'Rhumspringe', 'Seeburg', 'Radolfshausen', 'Landolfshausen', 'Seulingen', 'Herzberg', 'Osterode', 'Bad Lauterberg', 'Hattorf', 'Wulften', 'Walkenried'],
      'Weitere Orte': ['Dransfeld', 'Hann. Münden'],
    };
    return groups;
  }, []);

  // Important cities for the service matrix
  const topCities = useMemo(() => [
    'noerten-hardenberg', 'goettingen', 'bovenden', 'northeim', 'einbeck',
    'hardegsen', 'moringen', 'duderstadt', 'osterode', 'herzberg',
    'bad-lauterberg', 'hann-muenden', 'uslar', 'gieboldehausen', 'dransfeld',
    'hattorf', 'rosdorf', 'friedland', 'adelebsen', 'gleichen',
  ], []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Servicegebiet | Nessenius Garten & Landschaftsbau - 198 Orte im 30km Umkreis';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Nessenius Garten & Landschaftsbau ist in 198 Orten im 30km Umkreis von Nörten-Hardenberg tätig. Göttingen, Northeim, Einbeck, Duderstadt, Osterode und mehr. Jetzt Angebot anfragen!');
  }, []);

  return (
    <div className="service-area-page" data-testid="service-area-page">
      {/* Hero */}
      <section className="sa-hero">
        <div className="container">
          <div className="sa-hero-badge"><MapPin size={16} /> 30km Umkreis</div>
          <h1>Unser Servicegebiet</h1>
          <p className="sa-hero-sub">
            198 Orte im Umkreis von Nörten-Hardenberg. Von Göttingen bis Duderstadt, 
            von Northeim bis Herzberg – wir sind in Ihrer Nähe.
          </p>
          <div className="sa-hero-stats">
            <div className="sa-stat"><strong>198</strong><span>Orte</span></div>
            <div className="sa-stat"><strong>30km</strong><span>Radius</span></div>
            <div className="sa-stat"><strong>5</strong><span>Leistungen</span></div>
          </div>
        </div>
      </section>

      {/* Service × City Matrix - SEO Power Section */}
      <section className="sa-matrix">
        <div className="container">
          <h2>Alle Leistungen in Ihrer Stadt</h2>
          <p className="sa-section-intro">Klicken Sie auf Ihren Ort und die gewünschte Leistung:</p>
          <div className="sa-matrix-table">
            <div className="sa-matrix-header">
              <div className="sa-matrix-city-header">Stadt</div>
              {Object.entries(SERVICE_NAMES).map(([key, name]) => (
                <div key={key} className="sa-matrix-service-header">{name}</div>
              ))}
            </div>
            {topCities.map(citySlug => {
              const city = LOCATIONS[citySlug];
              if (!city) return null;
              return (
                <div key={citySlug} className="sa-matrix-row">
                  <div className="sa-matrix-city">{city.name}</div>
                  {Object.keys(SERVICE_NAMES).map(svcKey => (
                    <Link 
                      key={svcKey} 
                      to={`/${svcKey}/${citySlug}`}
                      className="sa-matrix-link"
                    >
                      <ArrowRight size={14} />
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* All Locations by Region */}
      <section className="sa-regions">
        <div className="container">
          <h2>Alle {totalLocationCount} Orte im Überblick</h2>
          {Object.entries(regionGroups).map(([groupName, regionNames]) => {
            const locations = regionNames.flatMap(rn => regionData[rn] || []);
            if (locations.length === 0) return null;
            return (
              <div key={groupName} className="sa-region-group">
                <h3>{groupName}</h3>
                <div className="sa-locations-tags">
                  {locations.sort((a, b) => a.distance - b.distance).map(loc => {
                    const label = (
                      <>
                        {loc.name}
                        {loc.distance > 0 && <span className="sa-loc-dist">{loc.distance}km</span>}
                      </>
                    );
                    // Ortsteile haben bewusst keine eigene Seite (sie wuerden
                    // nahezu identische Klonseiten erzeugen und werden per 301
                    // auf ihre Stadt weitergeleitet). Sie werden daher nur
                    // genannt, nicht verlinkt.
                    if (loc.type === 'Ortsteil') {
                      return (
                        <span key={loc.slug} className="sa-loc-tag sa-loc-tag--plain">
                          {label}
                        </span>
                      );
                    }
                    return (
                      <Link
                        key={loc.slug}
                        to={`/gartenpflege/${loc.slug}`}
                        className={`sa-loc-tag ${loc.type === 'Stadt' || loc.type === 'Kreisstadt' ? 'city' : ''}`}
                      >
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="sa-cta">
        <div className="container">
          <h2>Ihr Ort ist nicht dabei?</h2>
          <p>Kein Problem! Für größere Projekte fahren wir auch gerne etwas weiter. Kontaktieren Sie uns einfach.</p>
          <div className="sa-cta-buttons">
            <a href="tel:+4917655669090" className="btn-primary" data-testid="sa-call-btn">
              <Phone size={18} />
              0176 - 556 690 90
            </a>
            <Link to="/kontakt" className="btn-secondary" data-testid="sa-contact-btn">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceArea;
